import React, { CSSProperties } from "react";
import { getBorderColorForEvent } from "../helper";
import { ChalkboardEvent } from "../utils";
import { Text } from "components/common/Text";

const BORDER_SIZE = "0.35vw";

type ShapeConfig = {
  wrapperStyle: CSSProperties;
  wrapperClassName?: string;
  labelClassName?: string;
  innerElement?: React.ReactNode;
};

const getTriangleClipPath = (inverted?: boolean) =>
  inverted ? "50% 100%, 0 0, 100% 0" : "50% 0, 0 100%, 100% 100%";

const getShapeConfig = (
  shape: string,
  color: string,
  inverted?: boolean
): ShapeConfig => {
  const baseStyle: CSSProperties = {
    width: "5vw",
    height: "5vw",
    backgroundColor: color,
    borderWidth: BORDER_SIZE,
    borderColor: "currentColor",
    borderStyle: "solid",
  };

  switch (shape) {
    case "circle":
      return { wrapperStyle: { ...baseStyle, borderRadius: "50%" } };
    case "square":
      return { wrapperStyle: baseStyle };
    case "triangle": {
      const clip = getTriangleClipPath(inverted);
      const innerStyle: CSSProperties = {
        top: BORDER_SIZE,
        bottom: BORDER_SIZE,
        left: BORDER_SIZE,
        right: BORDER_SIZE,
        backgroundColor: color,
        clipPath: `polygon(${clip})`,
      };
      return {
        wrapperStyle: {
          width: "7vw",
          height: "6vw",
          backgroundColor: "currentColor",
          clipPath: `polygon(${clip})`,
        },
        innerElement: <div className="absolute" style={innerStyle} />,
      };
    }
    case "diamond":
      return {
        wrapperStyle: baseStyle,
        wrapperClassName: "rotate-45",
        labelClassName: "-rotate-45",
      };
    case "pentagon":
      return {
        wrapperStyle: {
          width: "5vw",
          height: "5vw",
          backgroundColor: color,
          clipPath: "polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)",
        },
      };
    default:
      return { wrapperStyle: baseStyle };
  }
};

type Props = {
  event: ChalkboardEvent;
  color: string;
  shape: string;
  onSelect: () => void;
};

export const EventMarker: React.FC<Props> = ({
  event,
  color,
  shape,
  onSelect,
}) => {
  const borderColor = getBorderColorForEvent(event);
  const inverted = shape === "triangle-inverted";
  const baseShape = inverted ? "triangle" : shape;
  const { wrapperStyle, wrapperClassName, labelClassName, innerElement } =
    getShapeConfig(baseShape, color, inverted);

  const sharedWrapperStyle: CSSProperties = {
    textShadow:
      "-1.5px -1.5px 0 #000, 1.5px -1.5px 0 #000, -1.5px 1.5px 0 #000, 1.5px 1.5px 0 #000",
  };

  return (
    <div
      className="absolute cursor-pointer"
      style={{
        left: `${event.x * 100}%`,
        top: `${event.y * 100}%`,
        transform: "translate(-50%, -50%)",
      }}
      onMouseDown={(ev) => {
        ev.stopPropagation();
        onSelect();
      }}
    >
      <div
        className={`relative z-[3] flex select-none items-center justify-center font-extrabold text-white ${
          wrapperClassName ?? ""
        } ${borderColor}`.trim()}
        style={{ ...sharedWrapperStyle, ...wrapperStyle }}
        data-testid={`event-${event.id}`}
      >
        {innerElement}
        <span
          className={`pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 font-extrabold text-white text-outline ${
            labelClassName ?? ""
          }`}
        >
          <Text variant="soccerChalkboardPlayerText">{event.playerNum}</Text>
        </span>
      </div>
    </div>
  );
};
